import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { TeamMember } from '@dynamika-shared/types/teamMember';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { DotsIcon, SuspendIcon, TrashIcon, EditIcon } from '@dynamika-shared/ui/icons';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { TeamMembersControlSchemaType } from '../../../uiControlSchemaByStatus/ControlTypes';
import { InstitutionInfo } from '@components/InstitutionInfo/InstitutionInfo';
import { OrganizationType } from '@dynamika-shared/types/inventory';
import { DateTime } from '@utils/DateTime';
import { MemberOptionType } from './ClinicalTrialMembers.view';
import { baseTeamMemberColumns } from '../../utils/baseTeamMember.columns';
import { loadingSkeleton, tooltipClassName } from '../../utils/tableUtils';

const ICON_PROPS = { width: 24, height: 24 };

const dropdownItems = (uiControlSchema: TeamMembersControlSchemaType) =>
  [
    {
      key: 'edit',
      label: <div>Edit</div>,
      icon: (
        <EditIcon
          width={18}
          height={18}
          className={'ml-[.4rem] pr-[.4rem]'}
        />
      ),
    },
    {
      key: 'suspend',
      label: <div>Suspend</div>,
      disabled: true,
      icon: (
        <SuspendIcon
          width={18}
          height={18}
          className={'ml-[.4rem] pr-[.4rem]'}
        />
      ),
    },
    {
      key: 'delete',
      label: <div className={'text-redOrange'}>Delete</div>,
      icon: <TrashIcon {...ICON_PROPS} className='text-redOrange' />,
    },
  ].filter(item => {
    return !(uiControlSchema?.deleteTeamMember.hidden && item.key === 'delete');
  });

export function makeTeamMemberColumns(
  onClick: (key: MemberOptionType, member: TeamMember) => void,
  uiControlSchema: TeamMembersControlSchemaType,
  institutions: OrganizationType[]
): ColumnType<TeamMember>[] {
  return [
    baseTeamMemberColumns[0],
    {
      name: 'organizationName',
      header: () => 'Institution',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${17}rem]` : `w-[${13}rem]`),
      getValue: row => {
        const institution = institutions.find(i => i.id === row.organizationId);
        return (
          <InstitutionInfo
            name={institution?.name}
            address={institution?.address}
          />
        );
      },
      hasSorting: true,
      thClassName: 'w-[28rem] min-w-[14%] max-w-[14%]',
    },
    {
      name: 'lastActivity',
      header: () => 'Last Active',
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        ></div>
      ),
      getValue: row =>
        row.lastActivity ? DateTime(row.lastActivity) : 'Not available',
      hasSorting: true,
      thClassName: 'w-[18rem] min-w-[18%] max-w-[18%]',
    },
    {
      name: 'enabled',
      header: () => 'Status',
      loading: () => (
        <div
          className={'h-2 w-[10rem] animate-pulse rounded-[5px] bg-brightGray'}
        ></div>
      ),
      getValue: row => (
        <CustomTooltip
          label={row.enabled ? 'Active' : 'Inactive'}
          className={tooltipClassName(row.enabled)}
        />
      ),
      hasSorting: true,
      thClassName: 'w-[10rem] min-w-[10%] max-w-[10%]',
    },
    baseTeamMemberColumns[1],
    {
      name: 'siteCodes',
      header: () => 'Site Codes',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${13}rem]` : `w-[${10}rem]`),
      getValue: row => (
        <CustomTooltip
          label={row.siteCodes ?? ''}
          className={tooltipClassName(row.enabled)}
        />
      ),
      hasSorting: true,
      thClassName: 'w-[11rem] min-w-[11%] max-w-[11%]',
    },
    {
      name: 'action',
      header: () => 'Action',
      loading: () => (
        <div className='h-3 w-3 animate-pulse rounded-md bg-brightGray'></div>
      ),
      getValue: member => {
        return (
          <div className='ml-1 flex items-center justify-center'>
            <Dropdown
              items={dropdownItems(uiControlSchema)}
              overlayClassName={'w-[20rem]'}
              trigger={['click']}
              onClick={e => onClick(e.key as MemberOptionType, member)}
            >
              <div
                className='flex h-5 cursor-pointer items-center justify-center'
                data-tesitid={`menuMember-${member.id}`}
              >
                <div className='rounded-3xl p-1 hover:bg-waterLight'>
                  <DotsIcon className='hover:text-black' />
                </div>
              </div>
            </Dropdown>
          </div>
        );
      },
      alignToRight: true,
      thClassName: 'w-[7rem] min-w-[5%]',
    },
  ];
}
